/**
 * TanStack Query hook for the customer ticket list (SPEC §8 MyTicketsPage).
 *
 * Wraps GET /api/v1/tickets with the supported filter/scope/sort query
 * string. Returns the array as-is; component layer handles empty /
 * loading / error presentation.
 *
 * Filter shape mirrors the agent inbox minus the assignee bits: the
 * customer table never shows an Assignee column so we don't expose the
 * filter or sort key for it. ``sort_by`` defaults to "last_activity_at"
 * so the most-recently-touched ticket sits on top - matches the agent
 * inbox default behavior.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  TicketPriority,
  TicketStatus,
  TicketSummary,
  TicketType,
} from "@/types/api";

export type TicketsScope = "mine" | "company";

/** Sort columns the customer list endpoint accepts. Keep in sync with
 * ``_CUSTOMER_LIST_SORT_FIELDS`` in backend/app/api/tickets_customer.py. */
export type CustomerListSortBy =
  | "ticket_number"
  | "status"
  | "priority"
  | "requester_name"
  | "subject"
  | "last_activity_at"
  | "updated_at";

export type SortOrder = "asc" | "desc";

export interface UseMyTicketsArgs {
  /** Filter by ticket status; omit for "all". */
  status?: TicketStatus;
  /** Compound preset: "all_open" = every non-terminal ticket (open,
   * pending, waiting_customer, waiting_third_party, reopened). When set,
   * the backend ignores ``status``. */
  view?: "all_open";
  /** Filter by priority; omit for "all". */
  priority?: TicketPriority;
  /** Filter by ticket type; omit for "all". */
  type?: TicketType;
  /** Customer-portal scope toggle. Defaults to "mine". */
  scope?: TicketsScope;
  /** Free-text search. Blank / undefined = no search (full list). With
   * scope="company" + the company-scope permission the server searches
   * the user's whole org; otherwise it searches only their own tickets.
   * Internal notes are never matched server-side. */
  q?: string;
  /** Sort column; defaults to last_activity_at on the server. */
  sort_by?: CustomerListSortBy;
  /** Sort direction; defaults to desc on the server. */
  sort_order?: SortOrder;
}

export function myTicketsQueryKey(args: UseMyTicketsArgs): readonly unknown[] {
  return [
    "tickets",
    "list",
    args.scope ?? "mine",
    args.status ?? "all",
    args.view ?? "no-view",
    args.priority ?? "all",
    args.type ?? "all",
    args.q?.trim() ?? "",
    args.sort_by ?? "last_activity_at",
    args.sort_order ?? "desc",
  ] as const;
}

export function useMyTickets(
  args: UseMyTicketsArgs = {},
): UseQueryResult<TicketSummary[]> {
  return useQuery({
    queryKey: myTicketsQueryKey(args),
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (args.status) params.status = args.status;
      if (args.view) params.view = args.view;
      if (args.priority) params.priority = args.priority;
      if (args.type) params.type = args.type;
      if (args.scope) params.scope = args.scope;
      if (args.q && args.q.trim()) params.q = args.q.trim();
      if (args.sort_by) params.sort_by = args.sort_by;
      if (args.sort_order) params.sort_order = args.sort_order;
      const response = await api.get<TicketSummary[]>("/tickets", { params });
      return response.data;
    },
  });
}
